import React, { useCallback, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Dymo from 'services/Dymo';
import MiscUtils from 'services/MiscUtils';
import Icon from 'components/Utils/NewIcon';
import DefaultModal from 'components/Utils/DefaultModal';
import EmergencyContactDetails from 'components/Patient/PatientInfo/EmergencyContactDetails';
import DrugLabel from 'components/Utils/DrugLabel';
import YellowCard from 'components/HealthRecords/Immunizations/YellowCard';
import NewAppointment from 'components/HealthRecords/Appointments/NewAppointment';
import './style.scss';
import DefaultButton from 'components/Utils/NewDefaultButton';
import { DefaultSwitch } from 'services/UIUtils';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';
import moment from 'moment-timezone';
import MarkDeceased from 'components/Patient/MarkDeceased';
import PatientClearancesEditor from 'components/Patient/PatientInfo/PatientClearancesEditor';
import LabPriceCalculator from 'components/Misc/LabPriceCalculator';
import IconButton from '@material-ui/core/IconButton';
import SendMultipleDocuments from 'components/HealthRecords/Documents/SendMultipleDocuments';
import PatientCommunication from 'components/Chat/PatientCommunication';
import NoteDraftHistory from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/NoteDraftHistory';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useDefaultPharmacy } from 'hooks/useDefaultPharmacy';
import { api } from 'careand-redux/api/ApiThunks';
import { InternOf, IsTypeNP, IsTypePhlebotomist } from 'careand-redux/selectors';
import PregnancyCalculatorModal from 'components/Modals/PregnancyCalculatorModal';
import PREVENTCalculatorModal from 'components/Modals/PREVENTCalculatorModal';
import FIB4CalculatorModal from 'components/Modals/FIB4CalculatorModal';
import PackYearCalculatorModal from 'components/Modals/PackYearCalculatorModal';
import AlcoholCalculatorModal from 'components/Modals/AlcoholCalculatorModal';
import { DefaultPharmacy } from 'components/HealthRecords/Prescriptions/NewPrescription/view';
import PharmacyBadge from 'components/Pharmacy/PharmacyBadge';
import { uiActions } from 'careand-redux/slices/ui/ui';
import StopBangCalculatorModal from 'components/Modals/StopBangCalculatorModal';
import { Device } from 'services/Native/Device';
import Dropzone from 'react-dropzone';
import { AddDocumentDocumentTypeEnum } from 'generated/api';
import CalendarUtils from 'services/CalendarUtils';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import { CustomLoadingDataSpinner } from 'components/Common/LoadingDataSpinner';
import { FOLLOW_UP_MESSAGES } from 'components/HealthRecords/Documents/BatchApproveDocuments';
import TextareaAutosize from 'components/Common/TextAreaAutosize';
import MenuItem from '@material-ui/core/MenuItem';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import SubscriptionUtils from 'services/SubscriptionUtils';
import { EditHandovers } from 'components/Schedule/Vacations/Handovers';
import PatientBilling from 'scenes/HomeStaff/PatientDirectory/PatientBilling';
import PediatricDoseCalculatorModal from 'components/Modals/PediatricDoseCalculatorModal';
import { Calculator } from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/AppointmentNoteSections';
import IVFCalculatorModal from 'components/Modals/IVFCalculatorModal';
import BMICalculatorModal from 'components/Modals/BMICalculatorModal';
import CrClCalculatorModal from 'components/Modals/CrClCalculatorModal';
import GenerateReportModal from './GenerateReportModal';

const DEFAULT_OBJECT = {};
const DEFAULT_ARRAY = [];

const MODALS = {
  appointment: 'appointment',
  emergency_contact: 'emergency_contact',
  drug_label: 'drug_label',
  export_hr: 'export_hr',
  immunization: 'immunization',
  notifications: 'notifications',
  send: 'send',
  sms: 'sms',
  deceased: 'deceased',
  drafts: 'drafts',
  lab_price: 'lab_price',
  pregnancy: 'pregnancy',
  pediatric: 'pediatric',
  alcohol: 'alcohol',
  smoking: 'smoking',
  pharmacy: 'pharmacy',
  stopBang: 'stopBang',
  handover: 'handover',
  billing: 'billing',
  generate_report: 'generate_report',
  patient_permissions: 'patient_permissions',
};

const ResourcesPanel = ({ patient, bubble, onClose }) => {
  const dispatch = useAppDispatch();
  const isPhlebotomist = useAppSelector(IsTypePhlebotomist);
  const isNP = useAppSelector(IsTypeNP);
  const isIntern = useAppSelector(InternOf);

  const [modal, setModal] = useState(undefined);
  const [searchFilter, setSearchFilter] = useState('');
  const isAddingPhoto = useIsLoading(api.Documents.addDocument);

  /**
   * @param {File[]} files
   */
  async function onPhoto(files) {
    if (files.length === 0) {
      return;
    }

    const date = moment().format('YYYY-MM-DD hh:mm:ss a');

    const doc = await dispatch(
      api.Documents.addDocument({
        id: patient.id,
        file: files[0],
        title: `Photo ${date}`,
        documentType: AddDocumentDocumentTypeEnum.Other,
        approved: false,
      }),
    );

    if (doc) {
      MiscUtils.showSuccess('Photo added succesfully');
    } else {
      MiscUtils.showToastrError('Failed to add photo', 'An error occurred adding the photo');
    }
  }

  const resources = useMemo(() => {
    return [
      {
        category: { name: 'Printing', icon: 'print' },
        items: [
          {
            disabled: !Device.isMobile(),
            icon: 'camera',
            searchText: 'Take Photo',
            element: (
              <Dropzone
                className="my-dropzone"
                accept="image/jpeg, image/png, image/heic"
                multiple={false}
                onDrop={onPhoto}
              >
                <CustomLoadingDataSpinner
                  loading={isAddingPhoto}
                  color="#42bcdd"
                  style={{
                    width: '15px',
                    height: '15px',
                    display: 'inline-block',
                    marginRight: '.2rem',
                  }}
                />
                {isAddingPhoto || (
                  <Icon src="camera" style={{ display: 'inline-block', marginRight: '.4rem' }} />
                )}
                <span>Take Photo</span>
              </Dropzone>
            ),
          },
          {
            icon: 'tag',
            element: <PrintPatientLabel patient={patient} />,
            searchText: 'Print Patient Label',
          },
          {
            icon: 'prescription-bottle',
            description: 'Print Drug Label',
            onClick: () => {
              setModal(MODALS.drug_label);
            },
          },
        ],
      },
      {
        category: { name: 'Documents & Reports', icon: 'files-medical' },
        items: [
          {
            icon: 'file-export',
            description: 'Export Health Records as PDF',
            onClick: () => {
              setModal(MODALS.export_hr);
            },
            disabled: isPhlebotomist,
          },
          {
            icon: 'file-chart-column',
            description: 'Generate Report',
            onClick: () => {
              setModal(MODALS.generate_report);
            },
            disabled: isPhlebotomist,
          },
          {
            icon: 'paper-plane',
            description: 'Send Documents',
            onClick: () => {
              setModal(MODALS.send);
            },
          },
          {
            icon: 'clock-rotate-left',
            description: 'Note Draft History',
            onClick: () => {
              setModal(MODALS.drafts);
            },
          },
        ],
      },
      {
        category: { name: 'Communication', icon: 'comments' },
        items: [
          {
            icon: 'inbox-full',
            description: 'Patient Communications',
            onClick: () => {
              setModal(MODALS.notifications);
            },
          },
          {
            icon: 'message-sms',
            description: 'Send SMS',
            onClick: () => {
              setModal(MODALS.sms);
            },
          },
        ],
      },
      {
        category: { name: 'Scheduling', icon: 'calendar-clock' },
        items: [
          {
            icon: 'calendar-plus',
            description: 'Book Appointment',
            onClick: () => {
              setModal(MODALS.appointment);
            },
          },
          {
            icon: 'right-left',
            description: 'Add to Handover',
            onClick: () => {
              setModal(MODALS.handover);
            },
          },
        ],
      },
      {
        category: { name: 'Patient Management', icon: 'user' },
        items: [
          {
            icon: 'phone',
            description: 'Emergency Contact',
            onClick: () => {
              setModal(MODALS.emergency_contact);
            },
          },
          {
            icon: 'prescription',
            description: 'Change Default Pharmacy',
            onClick: () => {
              setModal(MODALS.pharmacy);
            },
          },
          {
            icon: 'wallet',
            description: 'Patient Billing',
            onClick: () => {
              setModal(MODALS.billing);
            },
          },
          {
            icon: 'shield-check',
            description: 'Patient Permissions',
            onClick: () => setModal(MODALS.patient_permissions),
          },
          {
            icon: 'eye',
            description: 'View as Patient',
            onClick: async () => {
              const result = await dispatch(api.Patients.viewAsPatient({ patientId: patient.id }));
              if (result?.url) {
                window.open(result.url, '_blank', 'noopener');
              }
            },
          },
          {
            icon: 'skull',
            description: 'Mark Patient as Deceased',
            className: 'danger-item',
            onClick: () => {
              setModal(MODALS.deceased);
            },
            disabled: !isNP || isIntern,
          },
        ],
      },
      {
        category: { name: 'Calculators', icon: 'calculator', grid: true },
        items: [
          {
            searchText: 'BMI',
            element: (
              <Calculator
                name="BMI"
                type="bmi"
                calculator={<BMICalculatorModal patient={patient} />}
                simpleButton={true}
                patient={patient}
              />
            ),
          },
          {
            searchText: 'CrCl Creatinine Clearance Cockcroft Gault',
            element: (
              <Calculator
                name="CrCl"
                type="crcl"
                calculator={<CrClCalculatorModal patient={patient} />}
                simpleButton={true}
                patient={patient}
              />
            ),
          },
          {
            searchText: 'Alcohol',
            element: (
              <Calculator
                name="Alcohol"
                type="alcohol"
                calculator={<AlcoholCalculatorModal patient={patient} />}
                simpleButton={true}
                patient={patient}
              />
            ),
          },
          {
            searchText: 'PREVENT cardiovascular ASCVD risk',
            element: (
              <Calculator
                name="PREVENT"
                type="prevent"
                calculator={<PREVENTCalculatorModal patient={patient} />}
                simpleButton={true}
                patient={patient}
              />
            ),
          },
          {
            searchText: 'FIB-4',
            element: (
              <Calculator
                name="FIB-4"
                type="fib4"
                calculator={<FIB4CalculatorModal patient={patient} />}
                simpleButton={true}
                patient={patient}
              />
            ),
          },
          {
            searchText: 'Pediatric Dose',
            element: (
              <Calculator
                name="Pediatric Dose"
                type="pediatric-dose"
                calculator={<PediatricDoseCalculatorModal patient={patient} />}
                simpleButton={true}
                patient={patient}
              />
            ),
          },
          {
            searchText: 'Lab Price',
            element: (
              <Calculator
                name="Lab Price"
                type="lab-price"
                calculator={<LabPriceCalculator />}
                simpleButton={true}
                patient={patient}
              />
            ),
          },
          {
            searchText: 'Pack/Year',
            element: (
              <Calculator
                name="Pack/Year"
                type="pack-year"
                calculator={<PackYearCalculatorModal />}
                simpleButton={true}
                patient={patient}
              />
            ),
          },
          {
            searchText: 'Pregnancy',
            element: (
              <Calculator
                name="Pregnancy"
                type="pregnancy"
                calculator={<PregnancyCalculatorModal patient={patient} />}
                simpleButton={true}
                patient={patient}
              />
            ),
          },
          {
            searchText: 'IVF',
            element: (
              <Calculator
                name="IVF"
                type="ivf"
                calculator={<IVFCalculatorModal patient={patient} />}
                simpleButton={true}
                patient={patient}
              />
            ),
          },
          {
            searchText: 'STOP-BANG',
            element: (
              <Calculator
                name="STOP-BANG"
                type="stop-bang"
                calculator={<StopBangCalculatorModal patient={patient} />}
                simpleButton={true}
                patient={patient}
              />
            ),
          },
        ],
      },
    ];
  }, [patient, isPhlebotomist, isAddingPhoto]);

  const filteredResources = useMemo(() => {
    if (!searchFilter.trim()) return resources;
    const query = searchFilter.toLowerCase();
    return resources
      .map((category) => ({
        ...category,
        items: category.items.filter((item) => {
          const text = item.description || item.searchText || '';
          const categoryName = category.category.name || '';
          return text.toLowerCase().includes(query) || categoryName.toLowerCase().includes(query);
        }),
      }))
      .filter((category) => category.items.length > 0);
  }, [resources, searchFilter]);

  return (
    <div className="resources-panel full-height">
      <div className="row top-row physician-top-bar mobile-top-row">
        <div className="left-buttons">
          <IconButton onClick={onClose} className="close">
            <Icon className="white" src="xmark" />
          </IconButton>
        </div>
        <span className="large-title">Utilities</span>
      </div>
      <div className="search-filter-row">
        <Icon src="magnifying-glass" style={{ marginRight: '6px', opacity: 0.5 }} />
        <input
          type="text"
          className="search-filter-input"
          placeholder="Search tools..."
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
        />
        {searchFilter && (
          <Icon
            src="xmark"
            className="clickable"
            style={{ marginLeft: '4px', opacity: 0.5 }}
            onClick={() => setSearchFilter('')}
          />
        )}
      </div>
      {modal && (
        <>
          {modal === MODALS.export_hr && (
            <ExportCPPModal patient={patient} onClose={() => setModal()} />
          )}
          {modal === MODALS.appointment && (
            <BookingAppointmentModal
              bubble={bubble}
              patient={patient}
              open={true}
              onClose={() => setModal()}
            />
          )}
          {modal === MODALS.notifications && (
            <DefaultModal
              wrap={true}
              className="patient-communications-modal large-modal"
              title="Patient Notifications"
              padding={false}
              content={<PatientCommunication patient={patient} />}
              isOpen={true}
              onRequestClose={() => setModal()}
            />
          )}
          {modal === MODALS.sms && <SendSMSModal patient={patient} onClose={() => setModal()} />}
          {modal === MODALS.emergency_contact && (
            <DefaultModal
              wrap={true}
              key="emergency-contact-modal"
              className="emergency-contact-modal"
              title="Emergency Contact"
              content={<EmergencyContactDetails patient={patient} />}
              isOpen={true}
              onRequestClose={() => setModal()}
              draggable={true}
            />
          )}
          {modal === MODALS.drafts && (
            <DefaultModal
              wrap={true}
              padding={false}
              key="note-draft-modal"
              className="large-modal note-draft-modal"
              title="Note Draft History"
              content={<NoteDraftHistory patient={patient} onClose={() => setModal()} />}
              isOpen={true}
              onRequestClose={() => setModal()}
            />
          )}
          {modal === MODALS.drug_label && (
            <DefaultModal
              wrap={true}
              key="drug-label-modal"
              className="drug-label-modal fit-modal"
              title="Drug Label"
              padding={false}
              content={<DrugLabel patient={patient} />}
              isOpen={true}
              onRequestClose={() => setModal()}
              draggable={true}
            />
          )}
          {modal === MODALS.send && (
            <DefaultModal
              wrap={true}
              title="Send Documents"
              className="large-modal send-multiple-documents-modal"
              padding={false}
              rightHeaderButtons={
                <IconButton
                  onClick={() => {
                    dispatch(
                      uiActions.addPinnedDocument({
                        id: `send-multiple-${patient.id}`,
                        type: 'send_multiple_documents',
                        patientId: patient.id,
                      }),
                    );
                    setModal();
                  }}
                >
                  <Icon src="thumbtack" alt="Pin To Side" height="20px" width="20px" />
                </IconButton>
              }
              content={
                <SendMultipleDocuments
                  patient={patient}
                  onClose={() => setModal()}
                  allowPinToSide
                />
              }
              isOpen={true}
              onRequestClose={() => setModal()}
            />
          )}
          {modal === MODALS.deceased && (
            <DefaultModal
              wrap={true}
              title="Mark Patient as Deceased"
              className="deceased-modal"
              padding={true}
              content={<MarkDeceased patient={patient} onClose={() => setModal()} />}
              isOpen={true}
              onRequestClose={() => setModal()}
              draggable={true}
            />
          )}
          {modal === MODALS.immunization && (
            <DefaultModal
              className="large-modal"
              key="immunization-card-modal"
              content={<YellowCard patient={patient} onClose={() => setModal()} />}
              isOpen={true}
              onRequestClose={() => setModal()}
            />
          )}
          {modal === MODALS.lab_price && (
            <DefaultModal
              className="lab-price-calculator-modal"
              title="Lab Price Calculator"
              padding={true}
              wrap={true}
              content={<LabPriceCalculator />}
              isOpen={true}
              onRequestClose={() => setModal()}
              draggable={true}
            />
          )}
          {modal === MODALS.pregnancy && (
            <DefaultModal
              content={<PregnancyCalculatorModal patient={patient} />}
              isOpen={true}
              onRequestClose={() => setModal()}
              draggable={true}
            />
          )}
          {modal === MODALS.pediatric && (
            <DefaultModal
              content={<PediatricDoseCalculatorModal patient={patient} />}
              isOpen={true}
              onRequestClose={() => setModal()}
              draggable={true}
            />
          )}
          {modal === MODALS.alcohol && (
            <DefaultModal
              content={<AlcoholCalculatorModal patient={patient} />}
              isOpen={true}
              onRequestClose={() => setModal()}
              draggable={true}
            />
          )}
          {modal === MODALS.smoking && (
            <DefaultModal
              content={<PackYearCalculatorModal patient={patient} />}
              isOpen={true}
              onRequestClose={() => setModal()}
              draggable={true}
            />
          )}
          {modal === MODALS.pharmacy && (
            <DefaultModal
              title="Change Default Pharmacy"
              padding={true}
              wrap={true}
              content={<ChangePharmacy patient={patient} />}
              className="fit-modal"
              isOpen={true}
              onRequestClose={() => setModal()}
              draggable={true}
            />
          )}
          {modal === MODALS.stopBang && (
            <DefaultModal
              content={<StopBangCalculatorModal patient={patient} />}
              isOpen={true}
              onRequestClose={() => setModal()}
              draggable={true}
            />
          )}
          {modal === MODALS.handover && (
            <DefaultModal
              content={<EditHandovers patientId={patient.id} hideTitle={true} />}
              isOpen={true}
              title="Handovers"
              onRequestClose={() => setModal()}
              draggable={false}
              wrap={true}
              padding={false}
            />
          )}
          {modal === MODALS.billing && (
            <PatientBillingModal patient={patient} onClose={() => setModal()} />
          )}
          {modal === MODALS.patient_permissions && (
            <PatientPermissionsModal patient={patient} onClose={() => setModal()} />
          )}
          {modal === MODALS.generate_report && (
            <GenerateReportModal patient={patient} onClose={() => setModal()} />
          )}
        </>
      )}

      <div className="content padded-box scroll-list full-height">
        <ul key="static-resources">
          {filteredResources.map((category, categoryIndex) => (
            <ResourcesListItem category={category} key={categoryIndex} />
          ))}
          {filteredResources.length === 0 && searchFilter && (
            <li className="no-results">No tools match "{searchFilter}"</li>
          )}
        </ul>
      </div>
    </div>
  );
};

const ResourcesListItem = ({ category }) => {
  if (category.items.length <= 0) return null;

  return (
    <>
      <span className="large-title">{category.category.name}</span>
      <ul className={`category${category.category.grid ? ' category-grid' : ''}`}>
        {category.items
          .filter((item) => !item.disabled)
          .map((item, index) => {
            let className = `clickable${item.element ? '' : ' truncate'}${
              item.className ? ` ${item.className}` : ''
            }${category.category.grid ? ' grid-item' : ''}`;
            return (
              <li className={className} key={index} onClick={item.onClick}>
                {item.element || (
                  <>
                    {item.icon && (
                      <Icon
                        src={item.icon}
                        style={{
                          height: '11px',
                          marginRight: '6px',
                          opacity: 0.5,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <span>{item.description}</span>
                  </>
                )}
              </li>
            );
          })}
      </ul>
    </>
  );
};

const LABEL_SIZES = [
  { value: 'normal', label: 'Normal' },
  { value: 'small', label: 'Small' },
  { value: 'mini', label: 'Mini' },
];

const PrintPatientLabel = ({ patient, extraInfo }) => {
  const [number, setNumber] = useState(1);
  const [size, setSize] = useState('normal');

  const onPrint = useCallback(
    (number, patient, type) => {
      MiscUtils.showToastrInfo({
        title: 'Patient Label',
        message: `Printing ${number} ${type} labels, please wait...`,
      });
      for (let i = 0; i < number; i++) {
        Dymo.printPatientLabel(patient, extraInfo, type);
      }
    },
    [extraInfo],
  );

  return (
    <div className="print-label-row">
      <Icon src="tag" style={{ height: '11px', marginRight: '6px', opacity: 0.5, flexShrink: 0 }} />
      <span className="print-label-text" onClick={() => onPrint(number, patient, size)}>
        Print Patient Label{!!extraInfo ? ` ${extraInfo}` : ''}
      </span>
      <div className="print-label-controls">
        <div className="label-size-selector">
          {LABEL_SIZES.map((s) => (
            <span
              key={s.value}
              className={`size-option${size === s.value ? ' active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                setSize(s.value);
              }}
            >
              {s.label}
            </span>
          ))}
        </div>
        <div className="label-quantity">
          <span
            className="toggle left"
            onClick={(e) => {
              e.stopPropagation();
              setNumber(Math.max(number - 1, 1));
            }}
          >
            &lt;
          </span>
          {number}
          <span
            className="toggle right"
            onClick={(e) => {
              e.stopPropagation();
              setNumber(Math.min(number + 1, 99));
            }}
          >
            &gt;
          </span>
        </div>
      </div>
    </div>
  );
};

export const BookingAppointmentModal = ({
  bubble = DEFAULT_OBJECT,
  patient = DEFAULT_OBJECT,
  open,
  onClose,
  physician,
  startTime,
  clinicId,
}) => {
  const patientAppointments =
    useAppSelector((state) => state.appointments.byId[patient.id]) || DEFAULT_ARRAY;

  const address = useMemo(() => {
    let result;
    if (bubble.appointment_id) {
      let appointment = patientAppointments.find(
        (appointment) => appointment.id === bubble.appointment_id,
      );
      result = (appointment || {}).address;
    }
    return result;
  }, [patientAppointments, bubble]);

  return (
    open && (
      <DefaultModal
        className="large-modal"
        key="book-appointment-modal"
        title="Book Appointment"
        content={
          <NewAppointment
            patient={patient.id ? patient : undefined}
            allowClose={true}
            prefillAddress={address}
            onClose={onClose}
            physician={physician}
            startTime={startTime}
            clinicId={clinicId || physician?.clinic_id}
          />
        }
        isOpen={open}
        onRequestClose={onClose}
      />
    )
  );
};

const ChangePharmacy = ({ patient }) => {
  const dispatch = useAppDispatch();
  // Read the live patient from redux so "Current Pharmacy" reflects a freshly
  // saved default immediately — the `patient` prop can be a stale snapshot, in
  // which case useDefaultPharmacy would keep resolving the old default_pharmacy_id.
  const livePatient = useAppSelector((state) => state.patients.byId[patient?.id]) || patient;
  const pharmacy = useDefaultPharmacy(livePatient);
  const [deleting, setDeleting] = useState(false);

  async function removePharmacy() {
    if (pharmacy) {
      setDeleting(true);
      await dispatch(
        api.Patients.updatePatient({
          id: livePatient.id,
          patient: { ...livePatient, default_pharmacy_id: undefined },
        }),
      );
      setDeleting(false);
    }
  }

  return (
    <>
      <div className="row mb-4 full-width" style={{ minWidth: '250px' }}>
        <span className="small-title">Current Pharmacy</span>
        {pharmacy?.name !== undefined ? (
          <PharmacyBadge pharmacy={pharmacy} />
        ) : (
          <span className="mt-2">No Pharmacy</span>
        )}
      </div>
      <div className="row">
        <DefaultPharmacy patient={livePatient} />
        <DefaultButton
          name="Remove Pharmacy"
          hide={pharmacy?.id === undefined}
          onClick={removePharmacy}
          className="red"
          loading={deleting}
          style={{
            width: '100%',
            marginTop: '10px',
          }}
        />
      </div>
    </>
  );
};

const PatientPermissionsModal = ({ patient, onClose }) => {
  const dispatch = useAppDispatch();
  const initialClearances = patient.clearances || [];
  const [clearances, setClearances] = useState(initialClearances);
  const [saving, setSaving] = useState(false);

  const hasChanges = JSON.stringify(clearances) !== JSON.stringify(initialClearances);

  async function save() {
    setSaving(true);
    const result = await dispatch(
      api.Patients.updatePatient({
        id: patient.id,
        patient: { ...patient, clearances },
      }),
      {
        errorTitle: 'Patient Permissions',
      },
    );
    setSaving(false);
    if (result) {
      MiscUtils.showSuccess('Patient permissions updated successfully');
      onClose();
    }
  }

  return (
    <DefaultModal
      title="Patient Permissions"
      className="fit-modal"
      padding={true}
      wrap={true}
      draggable={true}
      content={
        <>
          <PatientClearancesEditor clearances={clearances} onChange={setClearances} />
          <div className="row mt-3">
            <DefaultButton
              name="Save"
              className="blue"
              loading={saving}
              disabled={!hasChanges}
              onClick={save}
              style={{ width: '100%' }}
            />
          </div>
        </>
      }
      isOpen={true}
      onRequestClose={onClose}
    />
  );
};

export const ExportCPPModal = ({ patient, onClose }) => {
  const dispatch = useAppDispatch();
  const [allRecords, setAllRecords] = useState(true);
  const [allDates, setAllDates] = useState(true);
  const [includeNotes, setIncludeNotes] = useState(true);
  const [includeDocuments, setIncludeDocuments] = useState(true);
  const [includeSummary, setIncludeSummary] = useState(true);
  const [from, setFrom] = useState(moment().unix());
  const [to, setTo] = useState(moment().unix());

  async function exportCPPWrapper() {
    MiscUtils.showToastrInfo({
      title: 'Export CPP',
      message:
        'Please wait a few seconds while your export is being generated. You will be notified once it has finished.',
    });

    let fromValue = !allRecords && !allDates ? from : undefined;
    let toValue = !allRecords && !allDates ? to : undefined;
    let excludeNotes = allRecords ? undefined : !includeNotes;
    let excludeDocuments = allRecords ? undefined : !includeDocuments;
    let excludeSummary = allRecords ? undefined : !includeSummary;

    let patientId = patient.id;
    onClose();

    await dispatch(
      api.HealthRecords.exportCPP({
        from: fromValue,
        to: toValue,
        patientId,
        excludeNotes,
        excludeDocuments,
        excludeSummary,
      }),
      {
        title: 'Export CPP',
        successText: 'CPP exported successfully',
      },
    );
  }

  return (
    <DefaultModal
      key="export-cpp-modal"
      title="Export CPP"
      className="export-cpp-modal"
      padding={true}
      wrap={true}
      draggable={true}
      content={
        <>
          <div className="row">
            <DefaultSwitch
              name="all_records"
              onChange={(event, isInputChecked) => setAllRecords(isInputChecked)}
              label="All Records"
              padded={false}
              reversed={true}
              checked={allRecords || false}
            />
          </div>
          {!allRecords && (
            <>
              <span className="small-title">Filters</span>
              <div className="row">
                <DefaultSwitch
                  name="allDates"
                  onChange={(event, isInputChecked) => setAllDates(isInputChecked)}
                  label="All Dates"
                  padded={false}
                  reversed={true}
                  checked={allDates || false}
                />
              </div>
              {!allDates && (
                <div className="row">
                  <DefaultDatePicker
                    label="From"
                    name="from"
                    value={from}
                    onChange={(event, date) => setFrom(date)}
                    style={{ width: 'calc(50% - 10px)', marginRight: '20px' }}
                  />
                  <DefaultDatePicker
                    label="To"
                    name="to"
                    value={to}
                    onChange={(event, date) => setTo(date)}
                    style={{ width: 'calc(50% - 10px)' }}
                  />
                </div>
              )}
              <DefaultSwitch
                name="includeNotes"
                onChange={(event, isInputChecked) => setIncludeNotes(isInputChecked)}
                label="Include Encounter Notes"
                padded={false}
                reversed={true}
                checked={includeNotes || false}
              />
              <DefaultSwitch
                name="includeDocuments"
                onChange={(event, isInputChecked) => setIncludeDocuments(isInputChecked)}
                label="Include Documents"
                padded={false}
                reversed={true}
                checked={includeDocuments || false}
              />
              <DefaultSwitch
                name="includeSummary"
                onChange={(event, isInputChecked) => setIncludeSummary(isInputChecked)}
                label="Include Summary"
                padded={false}
                reversed={true}
                checked={includeSummary || false}
              />
            </>
          )}
          <div className="row">
            <DefaultButton name="Export" style={{ marginTop: '15px' }} onClick={exportCPPWrapper} />
          </div>
        </>
      }
      isOpen={true}
      onRequestClose={onClose}
    />
  );
};

export const SendSMSModal = ({ patient, onClose }) => {
  const dispatch = useAppDispatch();
  const [messageTemplate, setMessageTemplate] = useState(FOLLOW_UP_MESSAGES[0]);
  const [message, setMessage] = useState('');
  const loading = useIsLoading(api.Patients.sendSMS);

  const patientId = patient.id;

  const patientSubscription = useAppSelector((state) => state.payments.subscriptions[patientId]);
  const isSubscriber = !SubscriptionUtils.isUnsubscribed(patientSubscription);

  async function sendSMS() {
    let response = await dispatch(
      api.Patients.sendSMS({
        id: patientId,
        stringResource: { value: message },
      }),
      {
        errorTitle: 'Send SMS',
        meta: true,
      },
    );
    if (response.isSuccess) {
      onClose();
    }
  }

  return (
    <DefaultModal
      title="Send SMS"
      className="send-sms-modal fit-modal"
      padding={true}
      wrap={true}
      draggable={true}
      content={
        <div className="column" style={{ width: '250px' }}>
          <div className="row">
            <DefaultSelectField
              label="Patient Text Message"
              style={{ width: '100%' }}
              value={messageTemplate.id}
              onChange={(event) => {
                let template = FOLLOW_UP_MESSAGES.find(
                  (template) => template.id === event.target.value,
                );
                setMessageTemplate(template);
                setMessage(template.text);
              }}
              inputElements={[
                ...FOLLOW_UP_MESSAGES.filter(
                  ({ subscriber, id }) => (!subscriber || isSubscriber) && id !== 'no_message',
                ).map((messageTemplate) => (
                  <MenuItem value={messageTemplate.id} key={'select-' + messageTemplate.id}>
                    {messageTemplate.value}
                  </MenuItem>
                )),
              ]}
            />
          </div>
          <div className="row">
            <TextareaAutosize
              name="Patient Text Message"
              value={message}
              disabled={messageTemplate.disabled}
              onChange={(event) => setMessage(event.target.value)}
            />
          </div>
          <div className="row mt-3">
            <DefaultButton
              loading={loading}
              className="blue"
              style={{ width: '100%' }}
              icon="message-sms"
              disabled={!message}
              name="Send SMS"
              onClick={sendSMS}
            />
          </div>
        </div>
      }
      isOpen={true}
      onRequestClose={onClose}
    />
  );
};

export const PatientBillingModal = ({ patient, onClose }) => {
  const dispatch = useAppDispatch();
  const navigateTo = useRef();
  const [receipt, setReceipt] = useState();

  const onSelectReceipt = useCallback((receipt) => {
    setReceipt(receipt);
    navigateTo.current?.(1);
  }, []);

  return (
    <DefaultModal
      title="Patient Billing"
      className=".patient-billing-modal large-modal"
      padding={true}
      wrap={true}
      draggable={false}
      content={<PatientBilling patient={patient} />}
      isOpen={true}
      onRequestClose={onClose}
    />
  );
};

ResourcesPanel.propTypes = {
  patient: PropTypes.object.isRequired,
  bubble: PropTypes.object,
  onClose: PropTypes.func,
};

export default ResourcesPanel;
